/**
 * 앨범 API
 * - 앨범 CRUD, 목록 조회
 */
import apiClient from './client';

export interface Album {
  _id: string;
  userId: string;
  title: string;
  artist: string;
  coverImageId?: string;
  coverImageUrl?: string;
  year?: string;
  genre?: string;
  tracklist: Array<{
    id: string;
    title: string;
    duration?: string;
  }>;
  printUnits: Record<string, any>;
  isPublic: boolean;
  sharedWith: string[];
  originalAlbumId?: string;
  originalOwnerId?: string;
  isCopied: boolean;
  copiedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AlbumListResponse {
  data: Album[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateAlbumData {
  title: string;
  artist: string;
  year?: string;
  genre?: string;
  tracklist?: Array<{
    id: string;
    title: string;
    duration?: string;
  }>;
  printUnits?: Record<string, any>;
  coverImageId?: string;
  coverImageUrl?: string;
}

/**
 * 앨범 생성
 */
export const createAlbum = async (data: CreateAlbumData): Promise<Album> => {
  const response = await apiClient.post<Album>('/albums', data);
  return response.data;
};

/**
 * 내가 생성한 앨범 목록
 */
export const getMyAlbums = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: string;
}): Promise<AlbumListResponse> => {
  const response = await apiClient.get<AlbumListResponse>('/albums/my', { params });
  return response.data;
};

/**
 * 공유받은 앨범 목록
 */
export const getSharedAlbums = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: string;
}): Promise<AlbumListResponse> => {
  const response = await apiClient.get<AlbumListResponse>('/albums/shared', { params });
  return response.data;
};

/**
 * 전체 앨범 목록 (내 앨범 + 공유받은 앨범)
 */
export const getAllAlbums = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: string;
}): Promise<AlbumListResponse> => {
  const response = await apiClient.get<AlbumListResponse>('/albums/all', { params });
  return response.data;
};

/**
 * 앨범 상세 조회
 */
export const getAlbum = async (id: string): Promise<Album> => {
  const response = await apiClient.get<Album>(`/albums/${id}`);
  return response.data;
};

/**
 * 앨범 불러오기 (EditorPage용)
 */
export const loadAlbum = async (id: string): Promise<Album> => {
  const response = await apiClient.get<Album>(`/albums/${id}/load`);
  return response.data;
};

/**
 * 앨범 수정
 */
export const updateAlbum = async (id: string, data: Partial<CreateAlbumData>): Promise<Album> => {
  const response = await apiClient.put<Album>(`/albums/${id}`, data);
  return response.data;
};

/**
 * 앨범 삭제
 */
export const deleteAlbum = async (id: string): Promise<void> => {
  await apiClient.delete(`/albums/${id}`);
};

/**
 * 앨범 복제
 * @param mode 'full' = 전체 복제 (기본값), 'designOnly' = 디자인만 복제 (텍스트 정보 제거)
 */
export const duplicateAlbum = async (id: string, mode: 'full' | 'designOnly' = 'full'): Promise<Album> => {
  const response = await apiClient.post<Album>(`/albums/${id}/duplicate`, { mode });
  return response.data;
};

/**
 * 북마크 토글
 */
export const toggleBookmark = async (albumId: string): Promise<{ bookmarked: boolean }> => {
  const response = await apiClient.post<{ bookmarked: boolean }>(`/albums/bookmark/${albumId}`);
  return response.data;
};

/**
 * 북마크한 앨범 목록
 */
export interface BookmarkedAlbum {
  _id: string;
  title: string;
  artist: string;
  coverImageUrl?: string;
  isPublic: boolean;
  likeCount: number;
  likes: string[];
  createdAt: string;
  tracklist?: Array<{ id: string; title: string; duration?: string }>;
}

export const getMyBookmarks = async (): Promise<{ data: BookmarkedAlbum[]; total: number }> => {
  const response = await apiClient.get<{ data: BookmarkedAlbum[]; total: number }>('/albums/bookmarks/me');
  return response.data;
};

/**
 * 인기 태그 목록 조회
 */
export const getPopularTags = async (limit = 20): Promise<{ tag: string; count: number }[]> => {
  const response = await apiClient.get<{ tag: string; count: number }[]>('/albums/tags/popular', { params: { limit } });
  return response.data;
};

